import type { ReactNode } from 'react';
import { cx } from '../cx';
import { heatSteps, stepOf } from './Heatmap';
import type { ChartLayout } from './layout';
import { SEQ_FILL, TICK_TEXT } from './scale';

export type PulseStripProps = {
  /** One value per week, oldest first (52 for a year); null for weeks before tracking began. */
  values: Array<number | null>;
  /** The labels under the first and last cell ("Oct 2025", "This week"). */
  start: ReactNode;
  end: ReactNode;
  /** Five ascending boundaries for the six steps; defaults to round numbers from the data. */
  steps?: number[];
  /** `auto` (default) switches to the narrow form in a narrow container: a shorter strip with no gaps between weeks. */
  layout?: ChartLayout;
  /** One sentence that states the pattern, for screen readers ("Hiring every month, busiest in spring"). */
  description: string;
  className?: string;
};

const STRIP: Record<ChartLayout, string> = { auto: 'gap-0 @md:gap-px', narrow: 'gap-0', wide: 'gap-px' };
const CELL: Record<ChartLayout, string> = { auto: 'h-4 @md:h-6', narrow: 'h-4', wide: 'h-6' };

/** A year of one employer's hiring in one row of weekly cells, in the sequential ramp: its pulse. */
export function PulseStrip({ values, start, end, steps, layout = 'auto', description, className }: PulseStripProps) {
  const bounds = steps ?? heatSteps(values.filter((v): v is number => v != null && v > 0));
  return (
    <div className={cx('@container flex flex-col gap-1', className)}>
      <div role="img" aria-label={description} className={cx('flex', STRIP[layout])}>
        {values.map((v, i) => (
          <span
            key={i}
            className={cx(
              'min-w-0 flex-1 rounded-xs',
              CELL[layout],
              v == null ? 'bg-jc-elevated' : v === 0 ? 'bg-jc-chart-grid' : SEQ_FILL[stepOf(v, bounds)],
            )}
          />
        ))}
      </div>
      <div className={cx('flex justify-between', TICK_TEXT)} aria-hidden="true">
        <span>{start}</span>
        <span>{end}</span>
      </div>
    </div>
  );
}
